import { AuditFunctionDTO, AuditInsightsDormantUserDTO, AuditInsightsEngagementRiskDTO, AuditInsightsHeatmapDTO, AuditInsightsHeatmapUserDTO, AuditInsightsPageUserDTO, AuditInsightsQueryDTO, AuditInsightsSummaryDTO, AuditInsightsTopEntityDTO, AuditInsightsTopFunctionDTO, AuditInsightsTopPageDTO, AuditInsightsTrendDTO, PageViewDTO } from 'src/app/core/models/system/audit/audit-insights.dto';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { EncryptionService } from '../../general/encryption-service';
import { environment } from 'src/environments/environment';
import { eSimApiPaths } from 'src/app/core/consts';
import { ExceptionsService } from '../../general/exceptions-service';
import { HttpClientService } from '../../general/http-client-service';
import { Injectable } from '@angular/core';
import { StringResultDTO } from 'src/app/core/interfaces/general/string-result';

@Injectable({ providedIn: 'root' })

export class AuditInsightsService {

  constructor(
    private encryptionService: EncryptionService,
    private exceptionsService: ExceptionsService,
    private httpClientService: HttpClientService
  ) { }

  private buildRangeParams(query: AuditInsightsQueryDTO): string {
    try {
      return '&startYear='  + query.startYear  +
             '&startMonth=' + query.startMonth +
             '&startDay='   + query.startDay   +
             '&endYear='    + query.endYear    +
             '&endMonth='   + query.endMonth   +
             '&endDay='     + query.endDay;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return '';
    }
  }

  private getWithCompany<T>(methodName : string,
                            companyId  : number,
                            path       : string,
                            extraParams: string): Observable<T> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true)
             .pipe(
               switchMap((encResult: StringResultDTO) => {
                 return this.httpClientService.get<T>(environment.eSimApi
                                                      + eSimApiPaths.AuditInsights
                                                      + '/'
                                                      + path
                                                      + '?companyIdEncSS=' + encodeURIComponent(encResult?.result ?? '')
                                                      + extraParams);
               }),
               catchError((error) => {
                 return throwError(() => new Error('AuditInsightsService.' + methodName + '() - ' + this.exceptionsService.formatHttpError(error)));
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public getDormantUsers(companyId: number, days: number): Observable<AuditInsightsDormantUserDTO[]> {
    try {
      return this.getWithCompany<AuditInsightsDormantUserDTO[]>('getDormantUsers', companyId, eSimApiPaths.AuditInsightsGetDormantUsers,
                                                                '&days=' + days);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public getEngagementRisk(query: AuditInsightsQueryDTO, dropPercent: number): Observable<AuditInsightsEngagementRiskDTO[]> {
    try {
      return this.getWithCompany<AuditInsightsEngagementRiskDTO[]>('getEngagementRisk', query.companyId, eSimApiPaths.AuditInsightsGetEngagementRisk,
                                                                   this.buildRangeParams(query) + '&dropPercent=' + dropPercent);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public getFunctions(companyId: number): Observable<AuditFunctionDTO[]> {
    try {
      return this.getWithCompany<AuditFunctionDTO[]>('getFunctions', companyId, eSimApiPaths.AuditInsightsGetFunctions, '');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public getHeatmap(query: AuditInsightsQueryDTO): Observable<AuditInsightsHeatmapDTO[]> {
    try {
      return this.getWithCompany<AuditInsightsHeatmapDTO[]>('getHeatmap', query.companyId, eSimApiPaths.AuditInsightsGetHeatmap,
                                                            this.buildRangeParams(query));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public getHeatmapUsers(query: AuditInsightsQueryDTO, dayOfWeek: number, hour: number): Observable<AuditInsightsHeatmapUserDTO[]> {
    try {
      return this.getWithCompany<AuditInsightsHeatmapUserDTO[]>('getHeatmapUsers', query.companyId, eSimApiPaths.AuditInsightsGetHeatmapUsers,
                                                                this.buildRangeParams(query) + '&dayOfWeek=' + dayOfWeek + '&hour=' + hour);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public getPageUsers(query: AuditInsightsQueryDTO, route: string): Observable<AuditInsightsPageUserDTO[]> {
    try {
      return this.getWithCompany<AuditInsightsPageUserDTO[]>('getPageUsers', query.companyId, eSimApiPaths.AuditInsightsGetPageUsers,
                                                             this.buildRangeParams(query) + '&route=' + encodeURIComponent(route));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public getSummary(query: AuditInsightsQueryDTO, comparePrevious: boolean): Observable<AuditInsightsSummaryDTO> {
    try {
      return this.getWithCompany<AuditInsightsSummaryDTO>('getSummary', query.companyId, eSimApiPaths.AuditInsightsGetSummary,
                                                          this.buildRangeParams(query) + '&comparePrevious=' + comparePrevious);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public getTopCompanies(query: AuditInsightsQueryDTO, top: number): Observable<AuditInsightsTopEntityDTO[]> {
    try {
      return this.getWithCompany<AuditInsightsTopEntityDTO[]>('getTopCompanies', query.companyId, eSimApiPaths.AuditInsightsGetTopCompanies,
                                                              this.buildRangeParams(query) + '&top=' + top);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public getTopFunctions(query: AuditInsightsQueryDTO, top: number, includeBackground: boolean): Observable<AuditInsightsTopFunctionDTO[]> {
    try {
      return this.getWithCompany<AuditInsightsTopFunctionDTO[]>('getTopFunctions', query.companyId, eSimApiPaths.AuditInsightsGetTopFunctions,
                                                                this.buildRangeParams(query) + '&top=' + top + '&includeBackground=' + includeBackground);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public getTopPages(query: AuditInsightsQueryDTO, top: number): Observable<AuditInsightsTopPageDTO[]> {
    try {
      return this.getWithCompany<AuditInsightsTopPageDTO[]>('getTopPages', query.companyId, eSimApiPaths.AuditInsightsGetTopPages,
                                                            this.buildRangeParams(query) + '&top=' + top);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public getTopUsers(query: AuditInsightsQueryDTO, top: number): Observable<AuditInsightsTopEntityDTO[]> {
    try {
      return this.getWithCompany<AuditInsightsTopEntityDTO[]>('getTopUsers', query.companyId, eSimApiPaths.AuditInsightsGetTopUsers,
                                                              this.buildRangeParams(query) + '&top=' + top);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public getTrend(query: AuditInsightsQueryDTO): Observable<AuditInsightsTrendDTO[]> {
    try {
      return this.getWithCompany<AuditInsightsTrendDTO[]>('getTrend', query.companyId, eSimApiPaths.AuditInsightsGetTrend,
                                                          this.buildRangeParams(query));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public logPageViews(views: PageViewDTO[]): Observable<void> {
    try {
      return this.httpClientService.post(environment.eSimApi
                                         + eSimApiPaths.AuditInsights
                                         + '/'
                                         + eSimApiPaths.AuditInsightsLogPageViews,
                                         views)
             .pipe(
               catchError((error) => {
                 return throwError(() => new Error('AuditInsightsService.logPageViews() - ' + this.exceptionsService.formatHttpError(error)));
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public updateFunction(companyId: number, item: AuditFunctionDTO): Observable<void> {
    try {
      if (!item?.functionKey)
        throw new Error('AuditInsightsService.updateFunction() - functionKey is required');

      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true)
             .pipe(
               switchMap((companyEnc: StringResultDTO) => {
                 return this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(item), true)
                        .pipe(
                          switchMap((objectEnc: StringResultDTO) => {
                            return this.httpClientService.put(environment.eSimApi
                                                              + eSimApiPaths.AuditInsights
                                                              + '/'
                                                              + eSimApiPaths.AuditInsightsUpdateFunction
                                                              + '?companyIdEncSS=' + encodeURIComponent(companyEnc?.result ?? '')
                                                              + '&objectEnc='      + encodeURIComponent(objectEnc?.result ?? ''),
                                                              undefined,
                                                              {});
                          })
                        );
               }),
               catchError((error) => {
                 return throwError(() => new Error('AuditInsightsService.updateFunction() - ' + this.exceptionsService.formatHttpError(error)));
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }
}
